import * as XLSX from 'xlsx';

function fmtData(s) {
  if (!s) return '';
  const [y, m, d] = s.split('-');
  return `${d}/${m}/${y}`;
}

function fmtMoeda(val) {
  if (val == null || val === '') return '-';
  return `R$ ${Number(val).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`;
}

function autoWidth(ws, rows) {
  if (!rows.length) return;
  const keys = Object.keys(rows[0]);
  ws['!cols'] = keys.map(k => ({
    wch: Math.min(Math.max(rows.reduce((mx, r) => Math.max(mx, String(r[k] ?? '').length), 0), k.length) + 2, 60),
  }));
}

function styledHeader(ws, range, fill = '001e45') {
  for (let C = range.s.c; C <= range.e.c; C++) {
    const addr = XLSX.utils.encode_cell({ r: 0, c: C });
    if (!ws[addr]) continue;
    ws[addr].s = {
      fill: { fgColor: { rgb: fill } },
      font: { bold: true, color: { rgb: 'FFFFFF' } },
      alignment: { horizontal: 'center', vertical: 'center' },
    };
  }
}

function infoSheet(viagem) {
  const rows = [
    { Campo: 'Título', Valor: viagem.titulo || '' },
    { Campo: 'Origem', Valor: viagem.origem || '' },
    { Campo: 'Destino', Valor: viagem.destino || '' },
    { Campo: 'Data', Valor: fmtData(viagem.data) },
    { Campo: 'Horário', Valor: viagem.horario || '' },
    { Campo: 'Veículo', Valor: viagem.veiculo || '' },
    { Campo: 'Motorista', Valor: viagem.motorista || '-' },
    { Campo: 'Capacidade', Valor: viagem.capacidade || 0 },
    { Campo: 'Status', Valor: viagem.status || '' },
  ];
  const ws = XLSX.utils.json_to_sheet(rows);
  ws['!cols'] = [{ wch: 18 }, { wch: 40 }];
  return ws;
}

function dl(wb, filename) {
  XLSX.writeFile(wb, `${filename}.xlsx`);
}

export function exportMotoristaXLSX(viagem) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, infoSheet(viagem), 'Info da Viagem');
  const reservas = (viagem.reservas || []).filter(r => r.status !== 'cancelada').sort((a, b) => (a.assento || 0) - (b.assento || 0));
  const rows = reservas.map(r => ({
    Assento: r.assento ?? '',
    Passageiro: r.cliente?.nome || 'Desconhecido',
    'CPF/Documento': r.cliente?.documento || '-',
    Telefone: r.cliente?.telefone || '-',
    Status: { confirmada: 'Confirmado', pendente: 'Pendente' }[r.status] || r.status,
    'Check-in': r.check_in ? 'Sim' : 'Não',
    'Local de Embarque': r.local_embarque || '-',
    'Local de Desembarque': r.local_desembarque || '-',
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  autoWidth(ws, rows);
  styledHeader(ws, XLSX.utils.decode_range(ws['!ref'] || 'A1'));
  XLSX.utils.book_append_sheet(wb, ws, 'Passageiros');
  dl(wb, `lista-motorista-${viagem.titulo || viagem.id}`);
}

export function exportBagagemXLSX(viagem, bagagens) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, infoSheet(viagem), 'Info da Viagem');
  const reservas = (viagem.reservas || []).filter(r => r.status !== 'cancelada').sort((a, b) => (a.assento || 0) - (b.assento || 0));
  const bagById = {};
  (bagagens || []).forEach(b => { bagById[b.reserva_id] = b; });
  const rows = reservas.map(r => {
    const b = bagById[r.id];
    return b ? {
      Assento: r.assento ?? '',
      Passageiro: r.cliente?.nome || b.passageiro_nome || 'Desconhecido',
      'Nº Malas': b.num_malas ?? 0,
      'Peso Total (kg)': b.peso_total_kg ?? 0,
      'Excesso (kg)': b.excesso_kg ?? 0,
      'Taxa Excesso': b.taxa_excesso > 0 ? fmtMoeda(b.taxa_excesso) : 'Sem excesso',
      'Código Bagagem': b.codigo_bagagem || '-',
      'Status Pgto': { pendente: 'Pendente', pago: 'Pago', isento: 'Isento' }[b.status_pagamento] || b.status_pagamento,
    } : {
      Assento: r.assento ?? '',
      Passageiro: r.cliente?.nome || 'Desconhecido',
      'Nº Malas': 'Não registrado', 'Peso Total (kg)': '-', 'Excesso (kg)': '-', 'Taxa Excesso': '-', 'Código Bagagem': '-', 'Status Pgto': '-',
    };
  });
  const ws = XLSX.utils.json_to_sheet(rows);
  autoWidth(ws, rows);
  styledHeader(ws, XLSX.utils.decode_range(ws['!ref'] || 'A1'));
  XLSX.utils.book_append_sheet(wb, ws, 'Bagagens');
  dl(wb, `lista-bagagem-${viagem.titulo || viagem.id}`);
}

export function exportEncomendasXLSX(viagem, encomendas) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, infoSheet(viagem), 'Info da Viagem');
  const sMap = { recebida: 'Recebida', em_transito: 'Em Trânsito', entregue: 'Entregue', cancelada: 'Cancelada' };
  const rows = (encomendas || []).map(e => ({
    'Código': e.codigo_interno || e.id?.slice(0, 8).toUpperCase() || '-',
    'Descrição': e.descricao || '-',
    'Remetente': e.remetente_nome || '-',
    'Tel. Remetente': e.remetente_telefone || '-',
    'Destinatário': e.destinatario_nome || '-',
    'Tel. Destinatário': e.destinatario_telefone || '-',
    'Local de Entrega': e.destino || '-',
    'Volumes': e.volumes ?? 1,
    'Valor': e.valor ? fmtMoeda(e.valor) : '-',
    'Status': sMap[e.status] || e.status || '-',
  }));
  const ws = rows.length ? XLSX.utils.json_to_sheet(rows) : XLSX.utils.aoa_to_sheet([['Nenhuma encomenda']]);
  if (rows.length) { autoWidth(ws, rows); styledHeader(ws, XLSX.utils.decode_range(ws['!ref'] || 'A1')); }
  XLSX.utils.book_append_sheet(wb, ws, 'Encomendas');
  dl(wb, `lista-encomendas-${viagem.titulo || viagem.id}`);
}

export function exportEmbarqueXLSX(viagem) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, infoSheet(viagem), 'Info da Viagem');
  const rows = (viagem.reservas || []).filter(r => r.status !== 'cancelada').sort((a, b) => (a.assento || 0) - (b.assento || 0)).map(r => ({
    Assento: r.assento ?? '',
    Passageiro: r.cliente?.nome || 'Desconhecido',
    'CPF/Documento': r.cliente?.documento || '-',
    Telefone: r.cliente?.telefone || '-',
    Status: { confirmada: 'Confirmado', pendente: 'Pendente' }[r.status] || r.status,
    'Check-in': r.check_in ? 'Sim' : 'Não',
    'Local de Embarque': r.local_embarque || '-',
    'Local de Desembarque': r.local_desembarque || '-',
    'ID Reserva': r.id || '-',
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  autoWidth(ws, rows);
  styledHeader(ws, XLSX.utils.decode_range(ws['!ref'] || 'A1'));
  XLSX.utils.book_append_sheet(wb, ws, 'Lista de Embarque');
  dl(wb, `lista-embarque-${viagem.titulo || viagem.id}`);
}

export function exportRelatorioXLSX(viagem) {
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, infoSheet(viagem), 'Info da Viagem');
  const reservas = (viagem.reservas || []).filter(r => r.status !== 'cancelada').sort((a, b) => (a.assento || 0) - (b.assento || 0));
  const receita = reservas.reduce((s, r) => s + (Number(r.valor) || 0), 0);
  const checkins = reservas.filter(r => r.check_in).length;
  const rows = reservas.map(r => ({
    Assento: r.assento ?? '',
    Passageiro: r.cliente?.nome || 'Desconhecido',
    'CPF/Documento': r.cliente?.documento || '-',
    Telefone: r.cliente?.telefone || '-',
    Status: { confirmada: 'Confirmada', pendente: 'Pendente' }[r.status] || r.status,
    Valor: r.valor ? fmtMoeda(r.valor) : '-',
    'Forma Pagamento': r.forma_pagamento || '-',
    'Check-in': r.check_in ? 'Sim' : 'Não',
    Vendedor: r.vendedor || '-',
    'Local de Embarque': r.local_embarque || '-',
    'Local de Desembarque': r.local_desembarque || '-',
  }));
  const ws = XLSX.utils.json_to_sheet(rows);
  autoWidth(ws, rows);
  styledHeader(ws, XLSX.utils.decode_range(ws['!ref'] || 'A1'));
  XLSX.utils.book_append_sheet(wb, ws, 'Passageiros');
  const resumo = XLSX.utils.json_to_sheet([
    { Item: 'Total Assentos', Valor: viagem.capacidade ?? 0 },
    { Item: 'Reservas', Valor: reservas.length },
    { Item: 'Assentos Livres', Valor: viagem.assentos_livres ?? (viagem.capacidade - reservas.length) },
    { Item: 'Check-in Realizado', Valor: checkins },
    { Item: 'Receita Total', Valor: fmtMoeda(receita) },
  ]);
  resumo['!cols'] = [{ wch: 28 }, { wch: 20 }];
  XLSX.utils.book_append_sheet(wb, resumo, 'Resumo Financeiro');
  dl(wb, `relatorio-passageiros-${viagem.titulo || viagem.id}`);
}
